// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import OrderSummaryCard, {
    OrderSummaryCardStyles,
    RecentOrderTableProperties,
} from "@vanmeter/Overrides/Components/OrderSummaryCard";

import Skeleton from "@vanmeter/App/Components/Skeleton/Skeleton";
import { getSettingsCollection } from "@vanmeter/App/Store/Selectors/SettingsSelectors";
import { OrderStatusQueryString } from "@vanmeter/XDL/constants";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    // XNG-Changes
    showOrderNumber = "showOrderNumber",
    showDate = "showDate",
    showOrderTotal = "showOrderTotal",
    showStatus = "showStatus",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showReorderProducts = "showReorderProducts",
    showReleaseNumber = "showReleaseNumber",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.showOrderNumber]: boolean;
        [fields.showDate]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.showStatus]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showReorderProducts]: boolean;
        [fields.showReleaseNumber]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const settingsCollection = getSettingsCollection(state);
    // XNG-Changes
    const isPunchOutSession = getIsPunchOutSession(state);

    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection,
        // XNG-Changes:from classic
        canViewOrders:
            isAuthenticated &&
            !isPunchOutSession &&
            ((session.userRoles || "").includes("Administrator") ||
                (session.userRoles || "").includes("Order History")),
        // XNG-Change
        tabs: (state as CustomAppState).xTabs,
        closeOrderStatuses: settingsCollection.customSettings.closeOrderStatuses?.split(","),
    };
};

const mapDispatchToProps = {
    loadOrders,
    // XNG-Change
    updateZone: (zone: string, enabled: boolean) => ({
        type: "xTabs/EnableZone",
        data: {
            zone,
            isEnabled: enabled, // we need to show this tab if we are in tab context
        },
    }),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    viewAllLinkStyle?: LinkPresentationProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}`;

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    // XNG-Changes: match classic sort order for stable recent-order results
    sort: "OrderDate DESC,ErpOrderNumber DESC,WebOrderNumber DESC",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            // XNG-Changes:from classic
            recentOrdersParameter.status = this.props.closeOrderStatuses;
            this.props.loadOrders(recentOrdersParameter);
        }
    }
    // XNG-change
    componentDidUpdate(): void {
        if (this.props.zone?.includes("tab")) {
            if (this.props.canViewOrders && this.props.ordersDataView.value && !this.props.tabs[this.props.zone]) {
                this.props.updateZone(this.props.zone, true);
            } else if (!this.props.canViewOrders || !this.props.ordersDataView.value) {
                this.props.updateZone(this.props.zone, false);
            }
        }
    }

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        // XNG-Changes
        if (canViewOrders && ordersDataView.isLoading) {
            return <Skeleton height="260px" />;
        }
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink
            ? `${orderHistoryPageLink.url}?${OrderStatusQueryString.closedOrders}=true`
            : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        // XNG-Changes
        const tableProperties: RecentOrderTableProperties = {
            date: {
                show: this.props.fields.showDate,
                title: translate("Date"),
            },
            orderNumber: {
                show: this.props.fields.showOrderNumber,
                title: translate("Order #"),
            },
            pONumber: {
                show: this.props.fields.showPONumber,
                title: translate("PO #"),
            },
            releaseNumber: {
                show: this.props.fields.showReleaseNumber,
                title: translate("Release #"),
            },
            shipTo: {
                show: this.props.fields.showShipTo,
                title: translate("Ship To / Pick Up"),
            },
            status: {
                show: this.props.fields.showStatus,
                title: translate("Status"),
            },
            orderTotal: {
                show: this.props.fields.showOrderTotal,
                title: translate("Total"),
            },
            reorderProducts: {
                show: this.props.fields.showReorderProducts,
                title: translate("Actions"),
            },
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                {ordersDataView.value.length === 0 && (
                    <Typography {...styles.noOrdersFoundText}>{translate("No orders found")}</Typography>
                )}

                {ordersDataView.value.length > 0 && (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            {Object.keys(tableProperties).map(key => {
                                const { show, title } = (tableProperties as any)[key];
                                return (
                                    show && (
                                        <DataTableHeader key={key} {...styles.dataTableHeaders}>
                                            {title}
                                        </DataTableHeader>
                                    )
                                );
                            })}
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {ordersDataView.value.map(
                                order =>
                                    order !== null && (
                                        <OrderSummaryCard
                                            key={order.id}
                                            order={order}
                                            orderSettings={settingsCollection.orderSettings}
                                            tableProperties={tableProperties}
                                            extendedStyles={styles.orderSummaryCard}
                                        />
                                    ),
                            )}
                        </DataTableBody>
                    </DataTable>
                )}
                {/*  XNG-Changes: View All Link */}
                <Link
                    href={orderHistoryUrl}
                    {...styles.viewAllLinkStyle}
                    // XNG-Changes
                    aria-label={translate("View Full Order History")}
                >
                    {translate("View Full Order History")}
                </Link>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            {
                name: fields.showOrderNumber,
                displayName: "Order #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showDate,
                displayName: "Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showOrderTotal,
                displayName: "Order Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showReorderProducts,
                displayName: "Reorder",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showReleaseNumber,
                displayName: "Release #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
        ],
    },
};

export default widgetModule;
